"use strict";
/**
 * Generic Euclidian Distance Converter Engine.
 *
 * Loads a converter pack (dimensions + methods) and provides:
 * - toVector(methodId, observation) → N-D vector
 * - fromVector(methodId, vector) → { observation, distance }
 * - convertMethodToMethod(fromId, toId, observation) → { data, matchDistance }
 *
 * No domain-specific logic — works with any set of dimensions and methods.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.EuclidianDistanceEngine = void 0;
// ─── Vector math ────────────────────────────────────────────────────────────
function zeroVector(dims) {
    const v = {};
    for (const d of dims)
        v[d] = 0;
    return v;
}
function vec(partial, dims) {
    return { ...zeroVector(dims), ...partial };
}
function clampVector(v, dims) {
    const r = { ...v };
    for (const d of dims)
        r[d] = Math.max(0, Math.min(1, r[d]));
    return r;
}
function weightedDistance(a, b, dims, w) {
    let sum = 0;
    for (const d of dims) {
        const diff = a[d] - b[d];
        sum += w[d] * diff * diff;
    }
    return Math.sqrt(sum);
}
function findClosest(vector, vocab, dims, w) {
    let best = vocab[0];
    let bestDist = Infinity;
    for (const entry of vocab) {
        const d = weightedDistance(vector, entry.vector, dims, w);
        if (d < bestDist) {
            bestDist = d;
            best = entry;
        }
    }
    return { observation: best.observation, distance: bestDist };
}
function valuesEqual(a, b, ci) {
    if (a === b)
        return true;
    if (ci && typeof a === 'string' && typeof b === 'string')
        return a.toUpperCase() === b.toUpperCase();
    return false;
}
function inferDims(comp, dims) {
    const s = new Set();
    for (const opt of comp.options)
        for (const k of Object.keys(opt.vector))
            s.add(k);
    return dims.filter(d => s.has(d));
}
function buildLookupConverter(def, dims, w) {
    const comps = def.components;
    const ci = def.caseInsensitive ?? false;
    const single = comps.length === 1;
    let vocabulary;
    if (single && !def.observations) {
        vocabulary = comps[0].options.map(o => ({ observation: o.value, vector: vec(o.vector, dims) }));
    }
    else if (def.observations) {
        vocabulary = def.observations.map(o => ({ observation: single ? o.select[comps[0].field] : { ...o.select }, vector: vec(o.vector, dims) }));
    }
    else {
        throw new Error(`lookup method "${def.methodId}" with ${comps.length} components requires observations[]`);
    }
    return {
        methodId: def.methodId,
        toVector(observation) {
            let entry;
            if (single) {
                entry = vocabulary.find(e => valuesEqual(e.observation, observation, ci));
            }
            else {
                const obs = observation;
                entry = vocabulary.find(e => {
                    const eo = e.observation;
                    return comps.every(c => valuesEqual(eo[c.field], obs[c.field], ci));
                });
            }
            if (!entry)
                throw new Error(`Unknown ${def.methodId} observation: ${JSON.stringify(observation)}`);
            return entry.vector;
        },
        fromVector(vector) {
            return findClosest(vector, vocabulary, dims, w).observation;
        },
        allObservations() {
            return vocabulary.map(e => e.observation);
        },
    };
}
function buildAssemblyConverter(def, dims, w) {
    const comps = def.components;
    const single = comps.length === 1;
    function findOption(comp, value) {
        return comp.options.find(o => {
            if (o.value === value)
                return true;
            if (typeof o.value === 'string' && typeof value === 'string')
                return o.value.toLowerCase() === value.trim().toLowerCase();
            return false;
        });
    }
    function toVector(observation) {
        const result = zeroVector(dims);
        for (const comp of comps) {
            const obsValue = single ? observation : observation[comp.field];
            const value = obsValue !== undefined ? obsValue : (comp.options[0].value ?? null);
            const opt = findOption(comp, value);
            if (!opt && value !== null && value !== undefined) {
                throw new Error(`Unknown ${comp.field} value: "${value}". Valid: ${comp.options.map(o => String(o.value)).join(', ')}`);
            }
            if (!opt)
                continue;
            for (const dim of dims) {
                const v = opt.vector[dim];
                if (v === undefined)
                    continue;
                const merge = opt.vectorMerge?.[dim] ?? 'sum';
                if (merge === 'override')
                    result[dim] = v;
                else if (merge === 'max')
                    result[dim] = Math.max(result[dim], v);
                else
                    result[dim] += v;
            }
        }
        return clampVector(result, dims);
    }
    // Check if per-component matching is safe
    const allHaveDims = comps.every(c => inferDims(c, dims).length > 0);
    const hasOverride = comps.some(c => c.options.some(o => o.vectorMerge && Object.values(o.vectorMerge).includes('override')));
    let dimsOverlap = false;
    if (allHaveDims) {
        const seen = new Set();
        for (const c of comps) {
            for (const d of inferDims(c, dims)) {
                if (seen.has(d)) {
                    dimsOverlap = true;
                    break;
                }
                seen.add(d);
            }
            if (dimsOverlap)
                break;
        }
    }
    const useDescriptive = allHaveDims && !hasOverride && !dimsOverlap;
    function descriptiveFromVector(vector) {
        const result = {};
        for (const comp of comps) {
            const cd = inferDims(comp, dims);
            let bestWord = comp.options[0].value;
            let bestDist = Infinity;
            for (const opt of comp.options) {
                let dist = 0;
                for (const d of cd) {
                    const diff = vector[d] - (opt.vector[d] ?? 0);
                    dist += diff * diff;
                }
                if (dist < bestDist) {
                    bestDist = dist;
                    bestWord = opt.value;
                }
            }
            result[comp.field] = bestWord;
        }
        return single ? result[comps[0].field] : result;
    }
    let _cachedVocab = null;
    function getVocabulary() {
        if (_cachedVocab)
            return _cachedVocab;
        const result = [];
        const indices = comps.map(() => 0);
        while (true) {
            const obs = single ? comps[0].options[indices[0]].value : Object.fromEntries(comps.map((c, i) => [c.field, c.options[indices[i]].value]));
            result.push({ observation: obs, vector: toVector(obs) });
            let carry = true;
            for (let i = comps.length - 1; i >= 0 && carry; i--) {
                indices[i]++;
                if (indices[i] < comps[i].options.length)
                    carry = false;
                else
                    indices[i] = 0;
            }
            if (carry)
                break;
        }
        return (_cachedVocab = result);
    }
    return {
        methodId: def.methodId,
        toVector,
        fromVector(vector) {
            if (useDescriptive)
                return descriptiveFromVector(vector);
            return findClosest(vector, getVocabulary(), dims, w).observation;
        },
        allObservations() {
            if (useDescriptive) {
                const base = {};
                for (const comp of comps)
                    base[comp.field] = comp.options[0].value;
                const result = [single ? base[comps[0].field] : { ...base }];
                for (const comp of comps) {
                    for (const opt of comp.options) {
                        if (opt.value !== comp.options[0].value) {
                            const variant = { ...base, [comp.field]: opt.value };
                            result.push(single ? variant[comps[0].field] : variant);
                        }
                    }
                }
                return result;
            }
            return getVocabulary().map(e => e.observation);
        },
    };
}
function createConverter(def, dims, w) {
    const isAssembly = def.components.length > 1 && !def.observations;
    return isAssembly ? buildAssemblyConverter(def, dims, w) : buildLookupConverter(def, dims, w);
}
/** Build a virtual '_raw' method from dimension stops in the converter config */
function buildRawMethod(dimensions, dimensionNames) {
    const components = [];
    for (const dimName of dimensionNames) {
        const dim = dimensions[dimName];
        if (!dim?.stops?.length)
            continue;
        const options = dim.stops.map(stop => ({
            value: stop.value,
            label: stop.label,
            vector: { [dimName]: stop.value },
        }));
        components.push({
            field: dimName,
            label: dim.shortLabel || dim.label,
            options,
        });
    }
    return {
        methodId: '_raw',
        order: 0,
        name: { en: 'HDS Native', fr: 'HDS Natif' },
        description: { en: 'Direct dimensional input using the HDS scale stops.', fr: 'Saisie dimensionnelle directe utilisant les paliers de l\'échelle HDS.' },
        components,
    };
}
// ─── Engine class ───────────────────────────────────────────────────────────
class EuclidianDistanceEngine {
    itemKey;
    eventType;
    dimensionNames;
    dimensions;
    weights;
    converterVersion;
    /** The pack's coverline rules, by id (labels and sources); empty for a pack without them. */
    temperatureRules;
    _converters = {};
    _methodDefs = {};
    _methodIds = [];
    constructor(pack) {
        this.itemKey = pack.itemKey;
        this.eventType = pack.eventType;
        this.converterVersion = pack.converterVersion;
        this.dimensionNames = pack.dimensionNames;
        this.dimensions = pack.dimensions;
        this.temperatureRules = pack.temperatureRules ?? {};
        // Build weights from dimensions
        this.weights = {};
        for (const dim of this.dimensionNames) {
            this.weights[dim] = pack.dimensions[dim]?.weight ?? 0;
        }
        // Build converters for each method
        for (const def of pack.methods) {
            this._methodDefs[def.methodId] = def;
            this._converters[def.methodId] = createConverter(def, this.dimensionNames, this.weights);
        }
        // Generate _raw virtual method from dimension stops
        const rawDef = buildRawMethod(pack.dimensions, this.dimensionNames);
        this._methodDefs[rawDef.methodId] = rawDef;
        this._converters[rawDef.methodId] = createConverter(rawDef, this.dimensionNames, this.weights);
        this._methodIds = pack.methods
            .slice()
            .sort((a, b) => (a.order ?? 999) - (b.order ?? 999))
            .map(d => d.methodId);
    }
    get methodIds() { return this._methodIds; }
    getMethodDef(methodId) {
        return this._methodDefs[methodId];
    }
    /** Convert an observation from a source method to an N-D vector */
    toVector(methodId, observation) {
        const c = this._converters[methodId];
        if (!c)
            throw new Error(`Unknown method: "${methodId}" in converter "${this.itemKey}"`);
        return c.toVector(observation);
    }
    /** Find the closest observation in a target method for a given vector */
    fromVector(methodId, vector) {
        const c = this._converters[methodId];
        if (!c)
            throw new Error(`Unknown method: "${methodId}" in converter "${this.itemKey}"`);
        const obs = c.fromVector(vector);
        const obsVec = c.toVector(obs);
        const dist = weightedDistance(vector, obsVec, this.dimensionNames, this.weights);
        return { data: obs, matchDistance: dist };
    }
    /** Convert between two methods */
    convertMethodToMethod(fromMethodId, toMethodId, observation) {
        const vector = this.toVector(fromMethodId, observation);
        return this.fromVector(toMethodId, vector);
    }
    /** Weighted Euclidean distance between two vectors */
    distance(a, b) {
        return weightedDistance(a, b, this.dimensionNames, this.weights);
    }
    /** Create a zero vector */
    zeroVector() {
        return zeroVector(this.dimensionNames);
    }
}
exports.EuclidianDistanceEngine = EuclidianDistanceEngine;
